// Attach and detach (nobgp-router#2139, MN-5, app#498): the words, the rules of the page and the
// wire shape, in one place. Pure, so that `node --experimental-strip-types` can test it.
//
// ⚠ The router decides. Each rule here only says, before the click, what the router will say:
// PERMISSIONS.md § 5 Attachment. The dialog shows the router's preview, `node_attach` with
// `dry_run`, before the user confirms.
//
// The arguments and the preview below are checked against `src/tools/node_attach.go`,
// `node_detach.go` and `node_alias.go` of router 0.4.212, and `opens_to_org` and
// `kept_private_nodes` against router 0.4.214. Nothing else here knows the wire.

import { mayAddNodes, type OrgRole } from './orgRole.ts'
import { ADD_NODE_REFUSED } from './networkSettings.ts'
import { PRIVATE_LABEL } from './nodeAccess.ts'

export type AttachOptions = {
  /** Writes nothing: the result is the preview. */
  dryRun?: boolean
  /** The name in this network, when the user changed the name of the preview. */
  name?: string
}

export const attachArgs = (nodeId: string, networkId: string, options: AttachOptions = {}): Record<string, unknown> => ({
  node: nodeId,
  network: networkId,
  ...(options.dryRun ? { dry_run: true } : {}),
  ...(options.name ? { name: options.name } : {}),
})

/**
 * One target of a name. ⚠ The router blanks `nodeName` and `network` for a node outside the
 * reader's orgs, and keeps the id: `node_alias_add` needs it.
 */
export type NameCandidate = { targetId: string; nodeName: string | null; network: string | null }

/** A bare name that stops resolving on this node, with the targets it would have. */
export type AmbiguousName = { name: string; candidates: NameCandidate[] }

export type AttachRefusal = { code: string; message: string }

/** What `node_attach` with `dry_run` says. */
export type AttachPreview = {
  /** The name the node would have in the network: `web-2` when its own name is taken there. */
  name: string
  /** The node is in the network already, so the attach changes nothing. */
  alreadyMember: boolean
  ambiguous: AmbiguousName[]
  /**
   * The node is not shared with its org now, and this attach shares it (router#2197). The router
   * sends it on a dry run after every gate. Absent: the attach does not open the node.
   */
  opensToOrg: boolean
  refusal: AttachRefusal | null
}

/** What `node_detach` says: the node's new name when it took a number in no network, else null. */
export const numberedName = (payload: unknown): string | null => {
  const row = (payload ?? {}) as { numbered?: unknown; name?: unknown }
  return row.numbered === true && typeof row.name === 'string' && row.name ? row.name : null
}

/** The names in a list of nodes of a result: `[{id, name}]`. */
const namesIn = (payload: unknown, key: string): string[] => {
  const rows = (payload as Record<string, unknown> | null)?.[key]
  return Array.isArray(rows) ? rows.flatMap(row => (typeof row?.name === 'string' && row.name ? [row.name] : [])) : []
}

/** The new names in `network_delete`'s `numbered`. */
export const numberedNames = (payload: unknown): string[] => namesIn(payload, 'numbered')

/**
 * The nodes that `node_detach` or `network_delete` set to Private (you and org admins), because
 * the removal would open them to their org and the caller does not control them (router#2200).
 */
export const keptPrivateNames = (payload: unknown): string[] => namesIn(payload, 'kept_private_nodes')

/** What `node_detach` says that the dialog must show. */
export type DetachResult = { numbered: string | null; keptPrivate: boolean }

export const detachResult = (payload: unknown): DetachResult => ({
  numbered: numberedName(payload),
  keptPrivate: keptPrivateNames(payload).length > 0,
})

/** The line after a detach that set the node to Private, because the caller does not control it. */
export const KEPT_PRIVATE_LINE = `This node is now set to ${PRIVATE_LABEL}, because you do not control it. Its owner, or an org owner or admin, can set it to follow its networks.`

/** The line after a network delete that set nodes to Private, because the caller does not control them. */
export const keptPrivateNodesLine = (names: string[]): string =>
  names.length === 1
    ? `This node is now set to ${PRIVATE_LABEL}, because you do not control it: ${names[0]}. Its owner, or an org owner or admin, can set it to follow its networks.`
    : `These nodes are now set to ${PRIVATE_LABEL}, because you do not control them: ${names.join(', ')}. The owner of each, or an org owner or admin, can set it to follow its networks.`

/** The line after a network delete that numbered names. */
export const numberedNodesLine = (names: string[]): string =>
  `These nodes took a number, because another node of this organization in no network has their name: ${names.join(', ')}. They keep these names. You can rename them.`

/** The line after a detach that numbered the node's name. */
export const numberedLine = (name: string): string =>
  `This node is now named ${name}, because another node of this organization in no network has its name. It keeps this name. You can rename it.`

/** A row of `candidates`, in `node_attach` and `node_name_check`: `{target_id, node, network}`. */
const candidatesOf = (value: unknown): NameCandidate[] =>
  Array.isArray(value)
    ? value.flatMap(item => {
        const row = item as { target_id?: unknown; node?: { name?: unknown } | null; network?: unknown } | null
        if (typeof row?.target_id !== 'string') return []
        return [
          {
            targetId: row.target_id,
            nodeName: typeof row.node?.name === 'string' && row.node.name ? row.node.name : null,
            network: typeof row.network === 'string' && row.network ? row.network : null,
          },
        ]
      })
    : []

/** The targets of a name, in words. A target the reader may not see is counted, never named. */
export const candidatesLine = (candidates: NameCandidate[]): string => {
  const seen = candidates.flatMap(({ nodeName, network }) =>
    nodeName ? [network ? `${nodeName} on ${network}` : nodeName] : []
  )
  const hidden = candidates.length - seen.length
  if (hidden) seen.push(hidden === 1 ? 'a node you cannot see' : `${hidden} nodes you cannot see`)
  return `It points at ${seen.join(', ')}.`
}

/**
 * The payload of a dry run. ⚠ An answer with no `name` keeps the node's own name: the dialog must
 * not print an empty name in a field the user confirms.
 */
export const parseAttachPreview = (payload: unknown, nodeName: string): AttachPreview => {
  const row = (payload ?? {}) as {
    name?: unknown
    already_member?: unknown
    ambiguous?: unknown
    opens_to_org?: unknown
    refusal?: unknown
  }
  const refusal = row.refusal as { code?: unknown; message?: unknown } | null | undefined
  return {
    name: typeof row.name === 'string' && row.name ? row.name : nodeName,
    alreadyMember: row.already_member === true,
    ambiguous: Array.isArray(row.ambiguous)
      ? row.ambiguous.flatMap(item => {
          const entry = item as { name?: unknown; candidates?: unknown }
          return typeof entry?.name === 'string' ? [{ name: entry.name, candidates: candidatesOf(entry.candidates) }] : []
        })
      : [],
    opensToOrg: row.opens_to_org === true,
    refusal: refusal ? { code: String(refusal.code ?? ''), message: String(refusal.message ?? '') } : null,
  }
}

type AttachNode = { ownerOrgId?: string | null }
type AttachNetwork = { orgId?: string | null; ownerId?: string | null; membersMayAddNodes?: boolean }

/** The line of a network that is in another organization than the node. */
export const OTHER_ORG_NETWORK = 'This network is in another organization than this node.'

/**
 * Why this reader cannot attach this node to this network, or null. The picker shows a network with
 * a reason as disabled, with the reason beside it.
 *
 * ⚠ The side of the NETWORK only. The side of the node, its controllers, decides who sees the
 * button at all. The same-org gate is the one place that blocks across orgs (MN-5).
 */
export const attachBlock = (
  node: AttachNode,
  network: AttachNetwork,
  role: OrgRole,
  userId: string | null | undefined
): string | null => {
  if (node.ownerOrgId && network.orgId && node.ownerOrgId !== network.orgId) return OTHER_ORG_NETWORK
  if (!mayAddNodes(role, network.ownerId, userId, network.membersMayAddNodes)) return ADD_NODE_REFUSED
  return null
}

/** A refusal of an attach: it needs the node's side AND the network's (PERMISSIONS.md § 5). */
export const ATTACH_FORBIDDEN =
  'To attach a node you must control it, and you must be allowed to add nodes to this network.'

/** A refusal of a detach: it needs either side. */
export const DETACH_FORBIDDEN =
  "Only this node's owner, the owner of this network, or an owner or admin of the organization can detach it."

/**
 * What an attach or a detach can change for the org (PERMISSIONS.md § 4.1). ⚠ True for every node, so
 * it needs no read of the node's or the network's access: the access panel says what is true now.
 */
export const SHARING_LINE =
  'A node that follows its networks is shared with the org only while every network it is in is Org. This change can open or close it.'

/** The line of an attach that the router says opens the node to the org (`opens_to_org`). */
export const OPENS_TO_ORG_LINE =
  'This attach shares the node with the org: every member of the org can use it, never as root.'

/** The sharing line of an attach, from the router's preview. */
export const attachSharingLine = (preview: Pick<AttachPreview, 'opensToOrg'>): string =>
  preview.opensToOrg ? OPENS_TO_ORG_LINE : SHARING_LINE

/**
 * The sharing line of a detach. ⚠ A detach by a person who does not control the node never opens
 * it: the router sets the node to Private first (router#2200). It can still close it.
 */
export const detachSharingLine = (controlsNode: boolean): string =>
  controlsNode
    ? SHARING_LINE
    : `A node that follows its networks is shared with the org only while every network it is in is Org. This change can close it, but it does not open it: you do not control this node, so it is set to ${PRIVATE_LABEL} instead.`

/**
 * The sharing line of a network delete. ⚠ A delete of a Private network can open a node that is
 * also in Org networks. The router opens only the nodes the caller controls, and sets each other
 * one to Private (router#2200). So the line is true for an Org network and for a Private one, and
 * the page needs no read of the network's access.
 */
export const NETWORK_DELETE_SHARING_LINE = `A node that follows its networks is shared with the org only while every network it is in is Org, so this delete can open or close a node. A node that it would open, and that you do not control, is set to ${PRIVATE_LABEL} instead.`

/** The first attach of a node starts its overlay (agent#1601). */
export const FIRST_ATTACH_LINE = 'The overlay starts on this node.'

export const LAST_DETACH_LINE =
  'This node leaves its last network. Its overlay stops and open connections close. You keep commands and files.'

/** The lines of a detach. `networkCount` is the networks the node is in now. */
export const detachLines = (networkCount: number, networkName: string): string[] =>
  networkCount <= 1
    ? [LAST_DETACH_LINE, `The node loses the network share at networks/${networkName}/.`]
    : [`Open connections through ${networkName} close. The node loses the network share at networks/${networkName}/.`]
